<!-- 
Example:
<k-tree 
	hoverable 
	rounded
	activatable
  :selectable="selectable"
  :selection-type="cascade"
  removable
  @remove="onRemove"
  v-model="selected"
  :active.sync="active" 
  :open="open_lists" 
  :search="search_value"
  :filter="item_filter"
  :items="items" 
>
  <template slot="prepend" slot-scope="{ item }">
    {{ item.id }}
  </template>
  <template slot="label" slot-scope="{ item }">
    {{ item.name }}
  </template>
</k-tree>

Selection types:
"leaf" (default): Includes children of the item when selected.
"cascade": Includes ancestors of the item during selection. Includes all descentants when deselected.
"independent": Does not include any other items when selected.

removable: Shows a remove button on the right side of each row. Clicking it emits a
"remove" event with { item, key, keys } where "keys" are all affected node keys,
collected following the same cascade behaviour as the active selection type
("cascade"/"leaf" include all descendants, "independent" affects only the item).
The component does not mutate the items prop; the parent is responsible for
removing the items from its own data using the emitted keys.
-->

<template>
	<div class="ks-tree" :class="{ 'ks-tree-dense': dense, 'ks-tree-hoverable': hoverable }">
		<k-tree-node
			v-for="item in displayItems"
			:key="item[itemKey]"
			:item="item"
			:level="0"
		>
			<template v-if="$scopedSlots.prepend" v-slot:prepend="props">
				<slot name="prepend" v-bind="props"></slot>
			</template>
			<template v-if="$scopedSlots.label" v-slot:label="props">
				<slot name="label" v-bind="props"></slot>
			</template>
			<template v-if="$scopedSlots.append" v-slot:append="props">
				<slot name="append" v-bind="props"></slot>
			</template>
		</k-tree-node>
	</div>
</template>

<script>
import KTreeNode from './KTreeNode.vue'

export default {
	name: 'KTree',

	components: {
		'k-tree-node': KTreeNode
	},

	provide: function () {
		return {
			kTree: this
		}
	},

	props: {
		items: { type: Array, default: function () { return [] } },
		itemKey: { type: String, default: 'id' },
		itemText: { type: String, default: 'name' },
		itemChildren: { type: String, default: 'children' },
		itemDisabled: { type: String, default: 'disabled' },
		value: { type: Array, default: function () { return [] } },
		open: { type: Array, default: function () { return [] } }, //(.sync)
		active: { type: Array, default: function () { return [] } }, //(.sync)
		selectable: { type: Boolean, default: false },
		activatable: { type: Boolean, default: false },
		multipleActive: { type: Boolean, default: false },
		openOnClick: { type: Boolean, default: false },
		openAll: { type: Boolean, default: false },
		dense: { type: Boolean, default: false },
		hoverable: { type: Boolean, default: false },
		rounded: { type: Boolean, default: false },
		shaped: { type: Boolean, default: false },
		removable: { type: Boolean, default: false },
		selectionType: { type: String, default: 'leaf' }, 
		returnObject: { type: Boolean, default: false },
		loadChildren: { type: Function, default: null },
		search: { type: String, default: '' },
		filter: { type: Function, default: null }
	},

	data: function () {
		return {
			openMap: {},
			activeMap: {},
			selectedMap: {},
			revision: 0
		}
	},

	computed: {
		displayItems: function () {
			var r = this.revision
			if (!this.search && !this.filter) return this.items
			return this._filterTree(this.items)
		}
	},

	watch: {
		open: {
			handler: function (val) {
				var map = {}
				val.forEach(function (k) { map[String(k)] = true })
				this.openMap = map
				this.revision++
			},
			immediate: true
		},
		active: {
			handler: function (val) {
				var map = {}
				val.forEach(function (k) { map[String(k)] = true })
				this.activeMap = map
				this.revision++
			},
			immediate: true
		},
		value: {
			handler: function (val) {
				var map = {}
				var self = this
				val.forEach(function (v) {
					var key = self.returnObject ? v[self.itemKey] : v
					map[String(key)] = true
				})
				this.selectedMap = map
				this.revision++
			},
			immediate: true
		},
		items: {
			handler: function () {
				if (this.openAll) {
					this._openAllNodes(this.items)
				}
				this.revision++
			},
			immediate: true,
			deep: true
		},
		search: function () {
			this.revision++
		}
	},

	created: function () {
		if (this.openAll) {
			this._openAllNodes(this.items)
		}
	},

	methods: {
		// ── API for KTreeNode (accessed via inject) ──

		isOpen: function (key) {
			return !!this.openMap[String(key)]
		},

		isActive: function (key) {
			return !!this.activeMap[String(key)]
		},

		isSelected: function (key) {
			return !!this.selectedMap[String(key)]
		},

		toggleOpen: function (key) {
			key = String(key)
			if (this.openMap[key]) {
				this.$delete(this.openMap, key)
			} else {
				this.$set(this.openMap, key, true)
			}
			this.revision++
			this.$emit('update:open', this._keysOf(this.openMap))
		},

		handleNodeClick: function (item) {
			if (!this.activatable) return
			var key = String(item[this.itemKey])
			if (this.multipleActive) {
				if (this.activeMap[key]) {
					this.$delete(this.activeMap, key)
				} else {
					this.$set(this.activeMap, key, true)
				}
			} else {
				var wasActive = !!this.activeMap[key]
				this.activeMap = {}
				if (!wasActive) {
					this.$set(this.activeMap, key, true)
				}
			}
			this.revision++
			this.$emit('update:active', this._keysOf(this.activeMap))
		},

		handleSelect: function (payload) {
			var self = this
			if (payload.cascade) {
				var ck = String(payload.key)
				if (this.selectedMap[ck]) {
					this.$delete(this.selectedMap, ck)
					if (payload.descendantKeys) {
						payload.descendantKeys.forEach(function (dk) {
							self.$delete(self.selectedMap, String(dk))
						})
					}
				} else {
					this.$set(this.selectedMap, ck, true)
				}
			} else if (payload.independent) {
				var ik = String(payload.key)
				if (this.selectedMap[ik]) {
					this.$delete(this.selectedMap, ik)
				} else {
					this.$set(this.selectedMap, ik, true)
				}
			} else if (payload.leaf) {
				var lk = String(payload.key)
				if (this.selectedMap[lk]) {
					this.$delete(this.selectedMap, lk)
				} else {
					this.$set(this.selectedMap, lk, true)
				}
			} else {
				if (payload.select) {
					payload.leafKeys.forEach(function (k) {
						self.$set(self.selectedMap, String(k), true)
					})
				} else {
					payload.leafKeys.forEach(function (k) {
						self.$delete(self.selectedMap, String(k))
					})
				}
			}
			this.revision++
			this._emitValue()
		},

		updateAll: function (val) {
			this.selectedMap = {}
			if (val) {
				this._selectAll(this.items)
			}
			this.revision++
			this._emitValue()
		},

		handleRemove: function (payload) {
			var self = this
			// Determine which keys are affected, following the selection-type cascade logic.
			var keys = [String(payload.key)]
			if (this.selectionType === 'cascade') {
				// Remove the item together with all of its descendants.
				(payload.descendantKeys || []).forEach(function (dk) {
					keys.push(String(dk))
				})
			} else if (this.selectionType === 'leaf') {
				// Remove the item together with all of its descendants (its leaves included).
				(payload.descendantKeys || []).forEach(function (dk) {
					keys.push(String(dk))
				})
			}
			// 'independent': only the item itself is affected.

			// Keep internal state in sync for the removed keys.
			keys.forEach(function (k) {
				self.$delete(self.selectedMap, k)
				self.$delete(self.openMap, k)
				self.$delete(self.activeMap, k)
			})
			this.revision++
			this.$emit('remove', { item: payload.item, key: payload.key, keys: keys })
			this.$emit('update:open', this._keysOf(this.openMap))
			this.$emit('update:active', this._keysOf(this.activeMap))
			this._emitValue()
		},

		// ── Internal ──

		_keysOf: function (map) {
			return Object.keys(map).filter(function (k) { return map[k] })
		},

		_emitValue: function () {
			var self = this
			var selectedKeys = this._keysOf(this.selectedMap)
			if (self.selectionType === 'cascade') {
				var ancestorKeys = {}
				selectedKeys.forEach(function (k) {
					var path = []
					self._findPath(self.items, k, path)
					path.forEach(function (a) {
						ancestorKeys[String(a[self.itemKey])] = true
					})
				})
				Object.keys(ancestorKeys).forEach(function (ak) {
					if (selectedKeys.indexOf(ak) === -1) {
						selectedKeys.push(ak)
					}
				})
			}
			if (this.returnObject) {
				var selectedItems = selectedKeys.map(function (k) {
					return self._findItem(self.items, k)
				}).filter(Boolean)
				this.$emit('input', selectedItems)
			} else {
				this.$emit('input', selectedKeys.map(function (k) {
					var n = Number(k)
					return isNaN(n) ? k : n
				}))
			}
		},

		_openAllNodes: function (items) {
			var self = this
			items.forEach(function (item) {
				var children = item[self.itemChildren] || []
				if (children.length > 0) {
					self.$set(self.openMap, String(item[self.itemKey]), true)
					self._openAllNodes(children)
				}
			})
		},

		_filterTree: function (items) {
			var self = this
			var result = []
			items.forEach(function (item) {
				var children = item[self.itemChildren] || []
				var matches = self._matchesFilter(item)
				
				if (matches) {
					result.push(Object.assign({}, item))
				} else {
					var filteredChildren = children.length > 0 ? self._filterTree(children) : []
					if (filteredChildren.length > 0) {
						var copy = Object.assign({}, item)
						copy[self.itemChildren] = filteredChildren
						result.push(copy)
					}
				}
			})
			return result
		},

		_matchesFilter: function (item) {
			if (this.filter) return this.filter(item, this.search, this.itemText)
			if (!this.search) return true
			var text = (item[this.itemText] || '').toString().toLowerCase()
			return text.indexOf(this.search.toLowerCase()) !== -1
		},

		_findItem: function (items, key) {
			for (var i = 0; i < items.length; i++) {
				if (String(items[i][this.itemKey]) === String(key)) return items[i]
				var children = items[i][this.itemChildren] || []
				if (children.length > 0) {
					var found = this._findItem(children, key)
					if (found) return found
				}
			}
			return null
		},

		_selectAll: function (items) {
			var self = this
			items.forEach(function (item) {
				var children = item[self.itemChildren] || []
				if (children.length === 0) {
					self.$set(self.selectedMap, String(item[self.itemKey]), true)
				} else {
					self._selectAll(children)
				}
			})
		},

		_findPath: function (items, targetKey, path) {
			var self = this
			for (var i = 0; i < items.length; i++) {
				var item = items[i]
				if (String(item[self.itemKey]) === String(targetKey)) return true
				var children = item[self.itemChildren] || []
				if (children.length > 0) {
					if (self._findPath(children, targetKey, path)) {
						path.push(item)
						return true
					}
				}
			}
			return false
		}
	}
}
</script>

<style>
/* ── Tree container ── */
.ks-tree {
	color: var(--ks-font-color-primary);
	font-size: 12px;
	user-select: none;
}
.ks-tree-dense .ks-tree-node-row {
	min-height: 32px;
	font-size: 11px;
}

/* ── Node row ── */
.ks-tree-node-row {
	display: flex;
	align-items: center;
	min-height: 30px;
	margin-bottom: 2px;
	padding-right: 8px;
	cursor: pointer;
	transition: background-color 0.15s;
}
.ks-tree-node-row:hover {
	background: var(--ks-background-color-light);
}
.ks-tree-node-row.ks-tree-node-active {
	background: var(--ks-col-secondary);
}
.ks-tree-node-row.ks-tree-node-active:hover {
	background: var(--ks-col-primary);
}
.ks-tree-node-row.ks-tree-node-selected {
	background: var(--ks-col-secondary);
}
.ks-tree-node-row.ks-tree-node-selected:hover {
	background: var(--ks-col-primary);
}
.ks-tree-node-row.ks-tree-node-disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.ks-tree-node-row.ks-tree-node-rounded {
	border-radius: 999em;
}
.ks-tree-node-row.ks-tree-node-shaped {
	border-radius: 0 999em 999em 0;
}

/* ── Toggle icon ── */
.ks-tree-node-toggle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	min-width: 24px;
	height: 24px;
}
.ks-tree-node-toggle-icon {
	font-size: 20px;
	color: var(--ks-font-color-secondary);
	transition: transform 0.15s;
}
.ks-tree-node-toggle-spacer {
	display: inline-block;
	width: 24px;
}

/* ── Checkbox ── */
.ks-tree-node-checkbox {
	display: flex;
	align-items: center;
	margin-right: 4px;
}

/* ── Label ── */
.ks-tree-node-label {
	flex: 1;
	padding: 0 6px;
	word-break: break-word;
}

/* ── Remove button ── */
.ks-tree-node-remove {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	min-width: 24px;
	height: 24px;
	margin-left: 4px;
	opacity: 0;
	cursor: pointer;
	transition: opacity 0.15s;
}
.ks-tree-node-row:hover .ks-tree-node-remove,
.ks-tree-node-row.ks-tree-node-active .ks-tree-node-remove,
.ks-tree-node-row.ks-tree-node-selected .ks-tree-node-remove {
	opacity: 1;
}
.ks-tree-node-remove-icon {
	font-size: 18px;
	color: var(--ks-font-color-secondary);
	transition: color 0.15s;
}
.ks-tree-node-remove:hover .ks-tree-node-remove-icon {
	color: var(--ks-col-danger, #e74c3c);
}

/* ── Loading spinner ── */
.ks-tree-node-loading {
	padding: 4px 0 4px 48px;
	color: var(--ks-font-color-secondary);
}
@keyframes ks-tree-spin-anim {
	from { transform: rotate(0deg) }
	to { transform: rotate(360deg) }
}
.ks-tree-spin {
	animation: ks-tree-spin-anim 0.8s linear infinite;
	font-size: 18px;
}
</style>
